// Copyright (c) 2022. Heusala Group Oy <info@heusalagroup.fi>. All rights reserved.

import {
    useCallback,
    useEffect,
    useRef,
    useState,
} from "react";
import { SendanorClientHttpService } from "../../core/services/SendanorClientHttpService";
import { LogService } from "../../../../io/hyperify/core/LogService";
import { createDomainSearchDTO, DomainSearchDTO } from "../../../../io/hyperify/core/store/types/domain/DomainSearchDTO";
import { DomainSearchState } from "../../../../io/hyperify/core/store/types/domain/DomainSearchState";

const LOG = LogService.createLogger('useDomainSearch');

const DOMAIN_SEARCH_RETRY_TIMEOUT_ON_ERROR : number = 3000;
const DOMAIN_SEARCH_DEBOUNCE_TIME : number = 500;
const DOMAIN_SEARCH_INTERVAL_TIME : number = 60000;

export type RefreshCallback = () => void;

export function useDomainSearch (
    searchString : string
) : [DomainSearchDTO | undefined , RefreshCallback] {

    const [ dto, setDto ] = useState<DomainSearchDTO | undefined>(undefined);

    const timeoutRef = useRef<any>();

    const searchCallback = useCallback(
        (query: string) => {

            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = undefined;
            }

            if (query !== '') {

                LOG.debug(`searchCallback: Searching "${query}"`);
                setDto(
                    createDomainSearchDTO(
                        query,
                        DomainSearchState.SEARCHING,
                        []
                    )
                );

                SendanorClientHttpService.searchDomain(query).then( (dto : DomainSearchDTO) => {
                    LOG.debug(`searchCallback: dto = `, dto);
                    setDto(dto);
                }).catch((err : any) => {
                    LOG.error(`searchCallback: Error while fetching clients: `, err);

                    if (timeoutRef.current) {
                        clearTimeout(timeoutRef.current);
                    }
                    timeoutRef.current = setTimeout(
                        () => {
                            LOG.debug(`searchCallback: clearing from timeout`);
                            setDto(undefined);
                        },
                        DOMAIN_SEARCH_RETRY_TIMEOUT_ON_ERROR
                    );
                });
            } else {
                LOG.debug(`searchCallback: Clearing empty DTO`);
                setDto(undefined);
            }
        },
        [
            setDto,
            timeoutRef,
        ]
    );

    const refreshCallback = useCallback(
        () => {
            searchCallback(searchString);
        },
        [
            searchString,
            searchCallback
        ]
    );

    // Calls refreshCallback() after a delay
    useEffect(
        () => {
            let timeout : any | undefined = setTimeout(
                () => {
                    timeout = undefined;
                    refreshCallback();
                },
                DOMAIN_SEARCH_DEBOUNCE_TIME
            );
            return () => {
                if (timeout !== undefined) {
                    clearTimeout(timeout);
                    timeout = undefined;
                }
            };
        },
        [
            refreshCallback
        ]
    );

    // Search in intervals
    useEffect(
        () => {
            let interval : any | undefined = setInterval(
                () => {
                    if (dto !== undefined && dto?.state === DomainSearchState.SEARCHING) {
                        refreshCallback();
                    }
                },
                DOMAIN_SEARCH_INTERVAL_TIME
            );
            return () => {
                if (interval !== undefined) {
                    clearInterval(interval);
                    interval = undefined;
                }
            };
        },
        [
            refreshCallback,
            dto
        ]
    );

    // Clear error timeout
    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = undefined;
            }
        };
    })

    return [dto, refreshCallback];
}
